import { useState } from 'react';

import { DocumentSendError, PueFault } from '@dreryk/egabinet-ezla-frontend-api';
import { InsuranceType } from '@dreryk/egabinet-ezla-published-language-api';
import { Visit } from '@dreryk/egabinet-visit-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { FormContent, StyledFlex } from '@dreryk/react-components';

import { commonTranslationKeys, medicalLeaveFormTranslationKeys } from '../../../translations';
import {
   findDefaultVisitDisease,
   isInsuredInForeign,
   isInsuredInPoland,
   mapMedicalLeavePayersToMedicalLeavePuePayers,
} from '../common/common.utils';
import { MedicalLeaveFormForeignPayerSection } from '../common/formSections/foreignPayer/MedicalLeaveFormPayerSection';
import { MedicalLeaveFormPayerSectionLoader } from '../common/formSections/foreignPayer/MedicalLeaveFormPayerSectionLoader';
import { MedicalLeaveFormLeaveDateRangeSection } from '../common/formSections/leaveDateRange/MedicalLeaveFormLeaveDateRangeSection';
import { MedicalLeaveFormSection } from '../common/formSections/MedicalLeaveFormSection';
import { MedicalLeaveFormPayerSection } from '../common/formSections/payer/MedicalLeaveFormPayerSection';
import { MedicalLeavePractitionerSection } from '../common/formSections/practitioner/MedicalLeavePractitionerSection';
import { MedicalLeaveFormStayAddressSection } from '../common/formSections/stayAdress/MedicalLeaveFormStayAddressSection';
import { MedicalLeaveFormCodes } from '../common/formSections/workInability/MedicalLeaveFormCodes';
import { MedicalLeaveFormCodesDescriptions } from '../common/formSections/workInability/MedicalLeaveFormCodesDescriptions';
import { MedicalLeaveFormMedicalIndications } from '../common/formSections/workInability/MedicalLeaveFormMedicalIndications';
import { MedicalLeavePueErrorsStatus } from '../common/MedicalLeaveErrorsStatus/MedicalLeavePueErrorsStatus';
import { StyledMedicalLeaveForm } from '../common/StyledMedicalLeaveForm';
import { StyledMedicalLeaveFormColumn } from '../common/StyledMedicalLeaveFormColumn';

import { MedicalLeaveSubmitAction } from './actionBar/MedicalLeaveSubmitAction/MedicalLeaveSubmitAction';
import { MedicalLeaveFormDiagnosisSection } from './diagnosis/MedicalLeaveFormDiagnosisSection';
import { getMedicalLeaveFormInitialValues } from './medicalLeave.constants';
import { useMedicalLeaveReasonIgnored } from './medicalLeave.hooks';
import { MedicalLeaveDraft, MedicalLeaveFormBean } from './medicalLeave.types';
import { MedicalLeaveZlaDocumentSendErrorsStatus } from './MedicalLeaveDocumentErrorsStatus/MedicalLeaveZlaDocumentSendErrorsStatus';
import { MedicalLeaveFormRouteLeavingGuard } from './MedicalLeaveFormRouteLeavingGuard';
import { MedicalLeaveFormPatientDataSection } from './sections/patientData/MedicalLeaveFormPatientDataSection';

type MedicalLeaveFormProps = {
   readonly visit: Visit;
   readonly draft?: MedicalLeaveDraft;
};

// eslint-disable-next-line max-lines-per-function
const MedicalLeaveForm = (props: MedicalLeaveFormProps): JSX.Element => {
   const [pueFault, setPueFault] = useState<PueFault | undefined>();
   const [zlaDocumentSendErrors, setZlaDocumentSendErrors] = useState<DocumentSendError[]>([]);
   const { visit, draft } = props;
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   const { payers, leaveAfterTerminationOfEmployment, diagnosis, insuranceType, ...otherDraftValues } = draft || {};
   const formController = useFormController<MedicalLeaveFormBean>({
      ...getMedicalLeaveFormInitialValues(),
      insuranceType:
         insuranceType ??
         visit.patient.externalIdentifier?.accept<InsuranceType>({
            visitPesel: () => 'ZUS',
            visitGuardianPesel: () => 'ZUS',
            visitPassport: () => 'OTHER_FOREIGN',
            visitNationalIdentificationNumber: () => 'OTHER_FOREIGN',
         }),
      diagnosis: diagnosis || findDefaultVisitDisease(visit),
      address: draft?.address || {
         city: visit.patient.address?.findTown()?.getName() ?? null,
         country: visit.patient.address?.getCountry() ?? null,
         flat: visit.patient.address?.findFlat()?.asString() ?? null,
         house: visit.patient.address?.findBuilding()?.asString() ?? null,
         postcode: visit.patient.address?.findPostcode() ?? null,
         street: visit.patient.address?.findStreet()?.asString() ?? null,
      },
      payers: mapMedicalLeavePayersToMedicalLeavePuePayers(payers),
      leaveAfterTerminationOfEmployment: leaveAfterTerminationOfEmployment ?? {},
      ...otherDraftValues,
   });

   const { insuranceType: insuranceTypeField, diagnosis: diagnosisField } = formController.watch([
      'insuranceType',
      'diagnosis',
   ]);
   const reasonIgnored = useMedicalLeaveReasonIgnored({ diagnosis: diagnosisField });

   return (
      <StyledFlex direction={'column'} gap={'12px'}>
         {zlaDocumentSendErrors.length > 0 ? (
            <MedicalLeaveZlaDocumentSendErrorsStatus errors={zlaDocumentSendErrors} />
         ) : null}
         {pueFault ? <MedicalLeavePueErrorsStatus fault={pueFault} /> : null}
         <StyledMedicalLeaveForm noValidate>
            <StyledMedicalLeaveFormColumn>
               <FormContent testId={'medicalLeave'}>
                  <MedicalLeaveFormSection title={commonTranslationKeys.workInabilityData}>
                     <StyledFlex direction='column'>
                        <StyledFlex gap={'20px'}>
                           <MedicalLeaveFormCodes controller={formController} />
                           <MedicalLeaveFormMedicalIndications controller={formController} />
                        </StyledFlex>
                        <MedicalLeaveFormCodesDescriptions />
                     </StyledFlex>
                  </MedicalLeaveFormSection>
                  <MedicalLeaveFormDiagnosisSection
                     register={formController.register}
                     visitDiagnoses={visit.diagnoses}
                     required
                  />
                  <MedicalLeaveFormLeaveDateRangeSection
                     controller={formController}
                     draftValuesReason={draft?.reason ?? undefined}
                     reasonFieldHidden={reasonIgnored}
                     title={medicalLeaveFormTranslationKeys.medicalLeaveDateRange}
                  />
                  {isInsuredInPoland(insuranceTypeField) && (
                     <MedicalLeaveFormPayerSection
                        patientId={props.visit.patient.id}
                        controller={formController}
                        externalIdentifier={visit.patient.referenceIdentifier?.findExternalIdentifier()}
                        noExternalIdentifierPlaceholder={medicalLeaveFormTranslationKeys.fillPatientData}
                        title={medicalLeaveFormTranslationKeys.payersData}
                        onPueFault={setPueFault}
                     />
                  )}
                  {isInsuredInForeign(insuranceTypeField) && (
                     <MedicalLeaveFormPayerSectionLoader patientId={visit.patient.id}>
                        {({ payersForeignRecentlyApproved }) => (
                           <MedicalLeaveFormForeignPayerSection
                              title={medicalLeaveFormTranslationKeys.payerData}
                              controller={formController}
                              payersForeignRecentlyApproved={payersForeignRecentlyApproved}
                           />
                        )}
                     </MedicalLeaveFormPayerSectionLoader>
                  )}
               </FormContent>
            </StyledMedicalLeaveFormColumn>
            <StyledMedicalLeaveFormColumn>
               <FormContent testId={'patientStayAddress'}>
                  <MedicalLeaveFormPatientDataSection patient={visit.patient} controller={formController} />
                  <MedicalLeaveFormStayAddressSection
                     title={medicalLeaveFormTranslationKeys.patientStayAddress}
                     register={formController.register}
                     watch={formController.watch}
                  />
                  <MedicalLeavePractitionerSection />
               </FormContent>
            </StyledMedicalLeaveFormColumn>
         </StyledMedicalLeaveForm>

         <MedicalLeaveFormRouteLeavingGuard
            active={formController.formState.isDirty}
            handleSubmitDraft={formController.handleSubmitDraft}
         />

         <MedicalLeaveSubmitAction
            formController={formController}
            onPueFault={setPueFault}
            visit={visit}
            onZlaDocumentSendFault={setZlaDocumentSendErrors}
         />
      </StyledFlex>
   );
};

export { getMedicalLeaveFormInitialValues, MedicalLeaveForm };
